// Editor do expediente — PÁGINA, não modal.
// Formulário + editor rico + pré-visualização não cabem num modal sem obrigar
// a rolar tendo tela sobrando, que é justamente o que não pode acontecer.
//
// Só rascunho é editável. Depois de finalizado o documento é imutável (o banco
// recusa com exceção, não só a tela) — erro se corrige cancelando e emitindo um
// substituto, para que a série conte a própria história.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert, App as AntApp, Button, Card, Col, DatePicker, Divider, Flex, Input,
  Modal, Row, Select, Space, Spin, Tag, Typography,
} from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, DeleteOutlined, EyeOutlined,
  SaveOutlined, UserAddOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import RichTextEditor from '../RichTextEditor';
import { salvarTemplate } from '../juridico-contratos/api';
import { usePerms } from '../../hooks/usePerms';
import {
  obterExpediente, atualizarExpediente, excluirExpediente, finalizarExpediente,
  previewExpediente, adicionarEnvolvido, removerEnvolvido, pdfExpediente, redigirComIA,
  Expediente, Envolvido, CampoSchema, VARIAVEIS_EXPEDIENTE,
} from './api';
import { assinantesPrevistos } from './assinantes';

const { Text, Title, Paragraph } = Typography;

const PAPEL_LABEL: Record<string, string> = {
  autor: 'Autor',
  signatario: 'Assina',
  copia: 'Cópia',
  destinatario_interno: 'Para',
};

export default function EditorExpediente() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { message, modal } = AntApp.useApp();
  // Documento jurídico desembarca no Jurídico — MAS a rota é gateada por
  // permissão, e finalizar só exige participar do expediente. Um signatário
  // sem o módulo seria jogado na Home sem explicação (achado da revisão
  // adversarial); para ele o destino continua sendo a lista de Expedientes.
  const { pode } = usePerms();

  const [exp, setExp] = useState<Expediente | null>(null);
  const [envolvidos, setEnvolvidos] = useState<Envolvido[]>([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [sujo, setSujo] = useState(false);

  // Estado do formulário (separado do `exp` pra não re-render o editor a cada tecla)
  const [form, setForm] = useState<Record<string, any>>({});
  const [corpo, setCorpo] = useState('');
  const corpoRef = useRef('');

  const [usuarios, setUsuarios] = useState<{ email: string; nome?: string }[]>([]);
  const [novoEnv, setNovoEnv] = useState<{ email: string; papel: string }>({ email: '', papel: 'signatario' });

  const [prev, setPrev] = useState<{ html: string; pendentes: string[]; faltando: string[] } | null>(null);

  // ── Pam: escreve o corpo a partir de um pedido em português ──────────────
  const [pamAberta, setPamAberta] = useState(false);
  const [pamPrompt, setPamPrompt] = useState('');
  const [pamPensando, setPamPensando] = useState(false);

  async function pedirPam(modo: 'substituir' | 'complementar') {
    if (!id || !pamPrompt.trim()) return;
    // Complementar precisa do texto que está na tela AGORA — e o que o backend
    // enxerga é o que foi salvo. Salva silencioso antes, como o preview faz.
    if (modo === 'complementar' && sujo && !(await salvar(true))) return;
    setPamPensando(true);
    try {
      const r = await redigirComIA(id, pamPrompt.trim(), modo);
      // ⚠️ O texto vive em DOIS lugares: `corpo` alimenta o editor e `corpoRef`
      //    é o que o salvar envia. O RichTextEditor sincroniza com
      //    emitUpdate:false, ou seja, escrever só no state NÃO dispara o
      //    onChange — o ref ficaria com o texto velho e o salvar mandaria ele.
      setCorpo(r.html);
      corpoRef.current = r.html;
      setSujo(true);
      setPamPrompt('');
      setPamAberta(false);
      if (r.faltas?.length) {
        message.warning({
          content: `A Pam deixou ${r.faltas.length} lacuna(s) marcada(s) no texto — procure por {{FALTA: no documento e preencha.`,
          duration: 8,
        });
      } else {
        message.success('Texto redigido. Revise antes de salvar.');
      }
    } catch (e: any) {
      const st = e?.response?.status;
      message.error(st === 409
        ? 'Documento já finalizado — não dá para reescrever.'
        : 'A Pam não conseguiu redigir: ' + (e?.response?.data?.error || e.message));
    } finally {
      setPamPensando(false);
    }
  }

  const carregar = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const d = await obterExpediente(id);
      if (!d.participo) {
        message.warning('Você não participa deste expediente.');
        nav('/administrativo/expedientes');
        return;
      }
      setExp(d.expediente);
      setEnvolvidos(d.envolvidos || []);
      setPodeEditar(!!d.pode_editar);
      setForm({
        assunto: d.expediente.assunto || '',
        data_documento: d.expediente.data_documento || null,
        local_emissao: d.expediente.local_emissao || '',
        destinatario_nome: d.expediente.destinatario_nome || '',
        destinatario_cargo: d.expediente.destinatario_cargo || '',
        destinatario_org: d.expediente.destinatario_org || '',
        destinatario_endereco: d.expediente.destinatario_endereco || '',
        dados_form: d.expediente.dados_form || {},
      });
      setCorpo(d.expediente.corpo_html || '<p></p>');
      corpoRef.current = d.expediente.corpo_html || '<p></p>';
      setSujo(false);
    } catch (e: any) {
      message.error('Falha ao carregar: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [id, nav, message]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const r = await axios.get(`${API_URL}/admin/usuarios`, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios(Array.isArray(d) ? d : []);
      } catch { setUsuarios([]); }   // sem permissão: cai no e-mail digitado
    })();
  }, []);

  // Avisa antes de sair com alteração não salva (não há autosave de propósito:
  // salvar é ato explícito, como finalizar).
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (sujo) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [sujo]);

  // Quem sai na linha de assinatura do PDF. Só vale para ofício/declaração/
  // memorando em rascunho: contrato assina pelas PARTES (outra regra, no
  // Jurídico) e numerado já tem PDF congelado ou envelope escolhido no envio.
  const mostraQuemAssina = !!exp && exp.status === 'rascunho' && exp.area !== 'juridico';
  const semLinha = exp?.sugere_assinatura === false;
  const assina = assinantesPrevistos(envolvidos);
  // O nome que o PDF vai imprimir. `nome_exibicao` só é gravado na primeira
  // geração de PDF, com o `name` do Cognito (só para interno); até lá o mesmo
  // `name` vem da lista de usuários. O PDF NUNCA imprime o e-mail: sem nome, a
  // linha sai em branco. A tela não pode prometer o e-mail no lugar — é
  // justamente o caso que o aviso tem de mostrar (externo, ou sem nome no Cognito).
  const nomeDe = (e: Envolvido) => e.nome_exibicao
    || (e.externo ? ''
      : usuarios.find(u => u.email?.toLowerCase() === e.usuario_email.toLowerCase())?.nome)
    || '';
  const nomesDe = (quem: Envolvido[]) => quem
    .map(e => nomeDe(e) || `${e.usuario_email} (linha sai sem nome)`).join(', ');
  const semNome = assina.quem.filter(e => !nomeDe(e));
  const nomesAssina = nomesDe(assina.quem);

  function setCampo(k: string, v: any) { setForm(f => ({ ...f, [k]: v })); setSujo(true); }
  function setDadoForm(chave: string, v: any) {
    setForm(f => ({ ...f, dados_form: { ...(f.dados_form || {}), [chave]: v } }));
    setSujo(true);
  }

  async function salvar(silencioso = false) {
    if (!id) return false;
    setSalvando(true);
    try {
      await atualizarExpediente(id, { ...form, corpo_html: corpoRef.current });
      setSujo(false);
      if (!silencioso) message.success('Rascunho salvo');
      return true;
    } catch (e: any) {
      message.error('Falha ao salvar: ' + (e.response?.data?.error || e.message));
      return false;
    } finally { setSalvando(false); }
  }

  async function verPreview() {
    if (!id) return;
    if (sujo && !(await salvar(true))) return;
    try {
      const d = await previewExpediente(id);
      setPrev({ html: d.corpo_html, pendentes: d.variaveis_pendentes || [], faltando: d.campos_faltando || [] });
    } catch (e: any) {
      message.error('Falha na prévia: ' + (e.response?.data?.error || e.message));
    }
  }

  async function baixarMinuta() {
    if (!id) return;
    if (sujo && !(await salvar(true))) return;
    try {
      const r = await pdfExpediente(id);
      window.open(r.url, '_blank');
    } catch (e: any) {
      message.error('Falha ao gerar o PDF: ' + (e.response?.data?.error || e.message));
    }
  }

  async function finalizar() {
    if (!id || !exp) return;
    if (sujo && !(await salvar(true))) return;
    // Quem assina é lido do SERVIDOR na hora: o PDF é gerado depois do COMMIT
    // com o que está lá, e outro participante pode ter mudado "Quem participa"
    // em outra aba desde a última carga desta tela.
    let naHora = assina;
    if (mostraQuemAssina && !semLinha) {
      try {
        const d = await obterExpediente(id);
        setEnvolvidos(d.envolvidos || []);
        naHora = assinantesPrevistos(d.envolvidos);
      } catch { /* sem rede: mostra a última carga; o finalizar abaixo falharia igual */ }
    }
    modal.confirm({
      title: exp.area === 'juridico'
        ? 'Finalizar e numerar este contrato?' : 'Finalizar e numerar este expediente?',
      width: 520,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            O sistema vai atribuir o próximo número da série <Text code>{exp.prefixo}</Text> deste
            ano e <strong>congelar o conteúdo</strong>.
          </Paragraph>
          {mostraQuemAssina && !semLinha && (
            <Paragraph style={{ marginBottom: 8 }}>
              Linha de assinatura: <strong>{nomesDe(naHora.quem) || 'ninguém'}</strong>
              {naHora.peloAutor && (
                <Text type="warning"> — como ninguém está marcado como "Assina", sai o autor.
                  Se outra pessoa deve assinar, volte e adicione-a em "Quem participa".</Text>
              )}
            </Paragraph>
          )}
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            Depois disso não dá para editar. Se houver erro, o caminho é cancelar
            com motivo e emitir um substituto — o número usado não volta.
          </Paragraph>
        </div>
      ),
      okText: 'Finalizar e numerar',
      cancelText: 'Voltar e revisar',
      onOk: async () => {
        try {
          const r = await finalizarExpediente(id);
          // CONTRATO desembarca no Jurídico, com o painel de assinatura já
          // aberto (?assinar=): o Lucas finalizou um distrato e caiu na lista
          // de Expedientes — que nem mostra documento da área jurídica.
          if (exp.area === 'juridico' && pode('juridico.contratos.ver')) {
            message.success(`${r.codigo} numerado. Agora é enviar para assinatura.`);
            nav(`/juridico/contratos?assinar=${id}`);
          } else if (exp.area === 'juridico') {
            message.success(`${r.codigo} numerado. O envio para assinatura é feito pelo `
              + 'módulo Jurídico, por quem tem acesso a ele.', 8);
            nav('/administrativo/expedientes');
          } else {
            message.success(`Expediente numerado: ${r.codigo}`);
            nav('/administrativo/expedientes');
          }
        } catch (e: any) {
          const msg = e.response?.data?.error || e.message;
          message.error(msg, 8);
          throw new Error(msg);
        }
      },
    });
  }

  async function excluir() {
    if (!id) return;
    modal.confirm({
      title: 'Excluir este rascunho?',
      content: 'Rascunho não tem número, então nada fica pendente na série. A exclusão é definitiva.',
      okText: 'Excluir', okButtonProps: { danger: true }, cancelText: 'Cancelar',
      onOk: async () => {
        await excluirExpediente(id);
        message.success('Rascunho excluído');
        nav(exp?.area === 'juridico' && pode('juridico.contratos.ver')
            ? '/juridico/contratos' : '/administrativo/expedientes');
      },
    });
  }

  // Troca quem assina a partir do campo "Assinatura": adiciona/remove só o
  // papel "Assina" em `envolvidos` (as mesmas rotas do cartão "Quem participa").
  // Recarrega SÓ os envolvidos — `carregar()` repõe o formulário do servidor e
  // apagaria o que a pessoa digitou e ainda não salvou.
  const [trocandoAssinatura, setTrocandoAssinatura] = useState(false);
  async function trocarAssinantes(novos: string[]) {
    if (!id) return;
    const atuais = envolvidos.filter(e => e.papel === 'signatario').map(e => e.usuario_email);
    const tem = (lista: string[], em: string) => lista.some(x => x.toLowerCase() === em.toLowerCase());
    const entrar = novos.filter(em => !tem(atuais, em));
    const sair = atuais.filter(em => !tem(novos, em));
    setTrocandoAssinatura(true);
    try {
      for (const em of entrar) {
        await adicionarEnvolvido(id, {
          usuario_email: em, papel: 'signatario',
          externo: usuarios.length > 0 ? !usuarios.some(u => u.email?.toLowerCase() === em.toLowerCase()) : false,
        });
      }
      for (const em of sair) await removerEnvolvido(id, em, 'signatario');
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    } finally {
      try { const d = await obterExpediente(id); setEnvolvidos(d.envolvidos || []); } catch { /* mantém */ }
      setTrocandoAssinatura(false);
    }
  }

  async function addEnvolvido() {
    if (!id || !novoEnv.email) return;
    try {
      const conhecido = usuarios.some(u => u.email?.toLowerCase() === novoEnv.email.toLowerCase());
      await adicionarEnvolvido(id, {
        usuario_email: novoEnv.email,
        papel: novoEnv.papel,
        externo: usuarios.length > 0 ? !conhecido : false,
      });
      setNovoEnv({ email: '', papel: novoEnv.papel });
      carregar();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    }
  }

  // ── Salvar como template (só contrato, 2026-08-31) ─────────────────────
  // A minuta que ficou boa vira ponto de partida dos próximos. O nome é a
  // identidade: salvar de novo com o mesmo nome ATUALIZA a minuta da casa.
  const [tplAberto, setTplAberto] = useState(false);
  const [tplNome, setTplNome] = useState('');
  const [tplSalvando, setTplSalvando] = useState(false);
  async function salvarComoTemplate() {
    if (!tplNome.trim()) { message.warning('Dê um nome ao template'); return; }
    setTplSalvando(true);
    try {
      const r = await salvarTemplate({ nome: tplNome.trim(), expediente_id: id! });
      message.success(r.atualizado
        ? `Template "${r.nome}" atualizado.`
        : `Template "${r.nome}" salvo. Ele aparece no Redigir contrato.`);
      setTplAberto(false); setTplNome('');
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    } finally { setTplSalvando(false); }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;
  if (!exp) return null;

  const campos: CampoSchema[] = exp.campos_schema || [];
  const readOnly = !podeEditar;

  // Campo "Assinatura" (30/09/2026, pedido do Gustavo): escolher quem assina
  // junto dos campos do documento, em vez de adivinhar pela aba "Quem
  // participa". NÃO é um dado novo: é o papel "Assina" de `envolvidos` — a
  // fonte única de acesso e da linha do PDF (docs/expedientes.md, "Fonte única
  // de acesso"). Escolher aqui aparece lá, e vice-versa.
  const autor = envolvidos.find(e => e.papel === 'autor');
  const signatariosEmails = envolvidos.filter(e => e.papel === 'signatario').map(e => e.usuario_email);
  const opcoesAssinatura = [
    ...usuarios.map(u => ({ value: u.email, label: u.nome ? `${u.nome} (${u.email})` : u.email })),
    // Quem já assina e não está na lista de usuários (externo, ou a lista não
    // carregou por falta de permissão) continua aparecendo e pode ser tirado.
    ...signatariosEmails
      .filter(em => !usuarios.some(u => u.email?.toLowerCase() === em.toLowerCase()))
      .map(em => ({ value: em, label: em })),
  ];
  const campoAssinatura = mostraQuemAssina && !semLinha ? (
    <div>
      <Text type="secondary" style={{ fontSize: 12 }}>Assinatura</Text>
      <Select mode="multiple" style={{ width: '100%' }} disabled={readOnly || trocandoAssinatura}
        loading={trocandoAssinatura} showSearch allowClear
        placeholder={autor ? `Ninguém escolhido: assina o autor (${nomeDe(autor) || autor.usuario_email})`
                           : 'Quem assina o documento'}
        value={signatariosEmails}
        onChange={(v: string[]) => trocarAssinantes(v)}
        filterOption={(i, o) => String(o?.label || '').toLowerCase().includes(i.toLowerCase())}
        options={opcoesAssinatura} />
      <Text type={assina.peloAutor || semNome.length ? 'warning' : 'secondary'}
        style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
        {assina.peloAutor
          ? 'Ninguém escolhido: a linha de assinatura sai com o autor. Escolha aqui quem assina.'
          : `Assina no PDF: ${nomesAssina}.`
            + (autor && !signatariosEmails.some(em => em.toLowerCase() === autor.usuario_email.toLowerCase())
              ? ' O autor não assina.' : '')}
      </Text>
    </div>
  ) : null;

  return (
    <div style={{ padding: 16 }}>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 12 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />}
            onClick={() => nav(exp.area === 'juridico' && pode('juridico.contratos.ver')
              ? '/juridico/contratos' : '/administrativo/expedientes')} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {exp.codigo || `${exp.tipo_nome} (rascunho)`}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {exp.tipo_nome}
              {exp.status === 'rascunho' && ' · sem número até você finalizar'}
            </Text>
          </div>
        </Space>
        <Space wrap>
          {podeEditar && (
            <Button danger icon={<DeleteOutlined />} onClick={excluir}>Excluir</Button>
          )}
          <Button icon={<EyeOutlined />} onClick={verPreview}>Pré-visualizar</Button>
          <Button onClick={baixarMinuta}>PDF {exp.status === 'rascunho' ? '(minuta)' : ''}</Button>
          {/* Contrato de tipo sensível não vira template: o corpo carrega CPF
              e remuneração, e template é visível a todos que redigem. O
              backend recusa também — o botão sumir é só a versão educada. */}
          {exp.area === 'juridico' && !exp.tipo_sensivel && (
            <Button onClick={() => { setTplNome(''); setTplAberto(true); }}>
              Salvar como template
            </Button>
          )}
          {podeEditar && (
            <>
              <Button icon={<SaveOutlined />} loading={salvando} onClick={() => salvar()}>
                Salvar rascunho
              </Button>
              <Button type="primary" icon={<CheckCircleOutlined />} onClick={finalizar}>
                Finalizar e numerar
              </Button>
            </>
          )}
        </Space>
      </Flex>

      {readOnly && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={exp.status === 'rascunho'
            ? 'Você participa deste expediente, mas não como autor ou signatário — pode ver, não editar.'
            : `Expediente ${exp.status}. O conteúdo está congelado.`} />
      )}
      {sujo && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Há alterações não salvas." />
      )}

      <Row gutter={16}>
        {/* ── Coluna esquerda: formulário ── */}
        <Col xs={24} lg={9}>
          <Card size="small" title="Identificação" style={{ marginBottom: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Assunto *</Text>
                <Input value={form.assunto} disabled={readOnly} maxLength={300}
                  onChange={(e) => setCampo('assunto', e.target.value)}
                  placeholder="Uma linha dizendo do que se trata" />
              </div>
              <Flex gap={8}>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Data *</Text>
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" disabled={readOnly}
                    value={form.data_documento ? dayjs(String(form.data_documento).slice(0, 10)) : null}
                    onChange={(d) => setCampo('data_documento', d ? d.format('YYYY-MM-DD') : null)} />
                </div>
                <div style={{ flex: 1 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Local</Text>
                  <Input value={form.local_emissao} disabled={readOnly}
                    onChange={(e) => setCampo('local_emissao', e.target.value)}
                    placeholder="Porto Alegre" />
                </div>
              </Flex>
            </Space>
          </Card>

          {exp.exige_destinatario && (
            <Card size="small" title="Destinatário" style={{ marginBottom: 12 }}>
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Input placeholder="Nome *" value={form.destinatario_nome} disabled={readOnly}
                  onChange={(e) => setCampo('destinatario_nome', e.target.value)} />
                <Input placeholder="Cargo" value={form.destinatario_cargo} disabled={readOnly}
                  onChange={(e) => setCampo('destinatario_cargo', e.target.value)} />
                <Input placeholder="Órgão / empresa" value={form.destinatario_org} disabled={readOnly}
                  onChange={(e) => setCampo('destinatario_org', e.target.value)} />
                <Input placeholder="Endereço" value={form.destinatario_endereco} disabled={readOnly}
                  onChange={(e) => setCampo('destinatario_endereco', e.target.value)} />
              </Space>
            </Card>
          )}

          {campos.length > 0 && (
            <Card size="small" title={`Campos de ${exp.tipo_nome.toLowerCase()}`} style={{ marginBottom: 12 }}>
              <Space direction="vertical" style={{ width: '100%' }} size={10}>
                {campos.map(c => (
                  <div key={c.chave}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {c.rotulo}{c.obrigatorio ? ' *' : ''}
                    </Text>
                    {c.tipo === 'select' ? (
                      <Select style={{ width: '100%' }} allowClear disabled={readOnly}
                        value={(form.dados_form || {})[c.chave] || undefined}
                        onChange={(v) => setDadoForm(c.chave, v)}
                        options={(c.opcoes || []).map(o => ({ value: o, label: o }))} />
                    ) : (
                      <Input value={(form.dados_form || {})[c.chave] || ''} disabled={readOnly}
                        placeholder={c.placeholder}
                        onChange={(e) => setDadoForm(c.chave, e.target.value)} />
                    )}
                  </div>
                ))}
                {campoAssinatura}
              </Space>
            </Card>
          )}
          {campos.length === 0 && campoAssinatura && (
            <Card size="small" title="Assinatura" style={{ marginBottom: 12 }}>
              {campoAssinatura}
            </Card>
          )}

          <Card size="small" title="Quem participa">
            <Paragraph type="secondary" style={{ fontSize: 11, marginBottom: 10 }}>
              Só quem está aqui enxerga o conteúdo. Os demais veem apenas o número,
              o tipo e a data no acervo.
            </Paragraph>
            <Space direction="vertical" style={{ width: '100%' }} size={6}>
              {envolvidos.map(e => (
                <Flex key={`${e.usuario_email}-${e.papel}`} justify="space-between"
                      align="center" gap={6}>
                  {/* Mesmo cuidado do formulário abaixo: sem `minWidth: 0` o
                      bloco da esquerda não encolhe e empurra a lixeira para
                      fora. Ficou mais provável desde que o nome passou a vir
                      completo do Cognito ("Gustavo Schefelbanis Araujo") em vez
                      do e-mail. O `title` devolve o nome inteiro no passar do
                      mouse, já que agora ele pode aparecer cortado. */}
                  <Flex align="center" gap={6} style={{ flex: 1, minWidth: 0 }}>
                    <Tag style={{ flexShrink: 0, marginInlineEnd: 0 }}
                      color={e.papel === 'autor' ? 'gold' : e.papel === 'signatario' ? 'green' : 'default'}>
                      {PAPEL_LABEL[e.papel] || e.papel}
                    </Tag>
                    <Text style={{ fontSize: 12, overflow: 'hidden',
                                   textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={e.nome_exibicao || e.usuario_email}>
                      {e.nome_exibicao || e.usuario_email}
                    </Text>
                    {e.externo && (
                      <Tag style={{ fontSize: 10, flexShrink: 0, marginInlineEnd: 0 }}>externo</Tag>
                    )}
                    {/* "Autor" é quem criou e dá acesso — não é assinatura. Sem
                        isto a tela parecia dizer que o autor assina sempre. */}
                    {mostraQuemAssina && !semLinha && e.papel === 'autor' && !assina.peloAutor
                      && !signatariosEmails.some(em => em.toLowerCase() === e.usuario_email.toLowerCase()) && (
                      <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>não assina</Text>
                    )}
                  </Flex>
                  {podeEditar && e.papel !== 'autor' && (
                    <Button type="text" size="small" danger icon={<DeleteOutlined />}
                      style={{ flexShrink: 0 }}
                      onClick={async () => {
                        try { await removerEnvolvido(exp.id, e.usuario_email, e.papel); carregar(); }
                        catch (err: any) { message.error(err.response?.data?.error || err.message); }
                      }} />
                  )}
                </Flex>
              ))}
            </Space>
            {mostraQuemAssina && !semLinha && (
              <Alert style={{ marginTop: 10, fontSize: 12 }} showIcon
                type={assina.peloAutor || semNome.length ? 'warning' : 'success'}
                message={<>Assina no PDF: <strong>{nomesAssina || 'ninguém'}</strong></>}
                description={[
                  assina.peloAutor
                    ? 'Ninguém está marcado como "Assina", então a linha sai com o autor.'
                      + (podeEditar ? ' Para outra pessoa assinar no seu lugar, adicione-a abaixo '
                        + 'como "Assina": você continua como autor, mas não assina.' : '')
                    : 'Só quem está marcado como "Assina" entra na linha de assinatura.'
                      + (assina.quem.some(e => envolvidos.some(a => a.papel === 'autor'
                          && a.usuario_email.toLowerCase() === e.usuario_email.toLowerCase()))
                        ? '' : ' O autor não assina.'),
                  semNome.length
                    ? 'Sem nome cadastrado, a linha sai em branco (o PDF não imprime e-mail). '
                      + 'Na assinatura eletrônica o nome é pedido no envio e sai no PDF enviado.'
                    : '',
                ].filter(Boolean).join(' ')} />
            )}
            {podeEditar && (
              <>
                <Divider style={{ margin: '10px 0' }} />
                {/* ⚠️ `minWidth: 0` no item que estica e `flexShrink: 0` nos de
                    largura fixa NÃO são enfeite. Item de flex tem `min-width:
                    auto` por padrão: ele não encolhe abaixo da largura do
                    próprio conteúdo. Sem isso, uma opção longa ("Gustavo
                    Schefelbanis Araujo (gustavo.araujo@hydrodataeng.com.br)")
                    estica o Select, empurra o botão de adicionar para fora do
                    cartão — onde ele fica por baixo da coluna do texto — e o
                    clique não chega nele. O botão está lá, só não é alcançável.
                    Relatado por quem usa em 16/08/2026. */}
                <Flex gap={6} align="center">
                  {usuarios.length ? (
                    <Select showSearch style={{ flex: 1, minWidth: 0 }} placeholder="Pessoa"
                      value={novoEnv.email || undefined}
                      onChange={(v) => setNovoEnv(n => ({ ...n, email: v }))}
                      filterOption={(i, o) => String(o?.label || '').toLowerCase().includes(i.toLowerCase())}
                      options={usuarios.map(u => ({ value: u.email, label: u.nome ? `${u.nome} (${u.email})` : u.email }))} />
                  ) : (
                    <Input style={{ flex: 1, minWidth: 0 }} placeholder="e-mail" value={novoEnv.email}
                      onChange={(e) => setNovoEnv(n => ({ ...n, email: e.target.value }))} />
                  )}
                  <Select style={{ width: 108, flexShrink: 0 }} value={novoEnv.papel}
                    onChange={(v) => setNovoEnv(n => ({ ...n, papel: v }))}
                    options={[
                      { value: 'signatario', label: 'Assina' },
                      { value: 'copia', label: 'Cópia' },
                      { value: 'destinatario_interno', label: 'Para' },
                    ]} />
                  <Button icon={<UserAddOutlined />} onClick={addEnvolvido}
                    style={{ flexShrink: 0 }} />
                </Flex>
              </>
            )}
          </Card>
        </Col>

        {/* ── Coluna direita: o texto ── */}
        <Col xs={24} lg={15}>
          <Card size="small" title="Texto do documento"
            extra={podeEditar ? (
              <Button size="small" type={pamAberta ? 'default' : 'text'}
                icon={<span role="img" aria-label="Pam">✨</span>}
                onClick={() => setPamAberta((v) => !v)}>
                Pam redigir
              </Button>
            ) : (
              <Text type="secondary" style={{ fontSize: 11 }}>
                cabeçalho e rodapé entram no PDF automaticamente
              </Text>
            )}>

            {pamAberta && podeEditar && (
              <div style={{ marginBottom: 10, padding: 10, background: '#f6f7f9', borderRadius: 6 }}>
                <Input.TextArea
                  value={pamPrompt}
                  onChange={(e) => setPamPrompt(e.target.value)}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  placeholder="Diga o que o documento tem que dizer. Ex.: declarar que os estudos aumentaram as APPs em 33%, que a base do êxito era R$ 15.600 e o valor final ficou em R$ 11.686,20 — explicando a conta."
                  disabled={pamPensando}
                />
                <Flex justify="space-between" align="center" style={{ marginTop: 8 }} gap={8} wrap>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    A Pam não inventa dado: onde faltar, ela deixa um marcador <Text code style={{ fontSize: 10 }}>{'{{FALTA: ...}}'}</Text> no texto.
                  </Text>
                  <Space size={6}>
                    <Button size="small" onClick={() => pedirPam('complementar')}
                      loading={pamPensando} disabled={!pamPrompt.trim()}>
                      Complementar
                    </Button>
                    <Button size="small" type="primary" onClick={() => pedirPam('substituir')}
                      loading={pamPensando} disabled={!pamPrompt.trim()}>
                      Redigir
                    </Button>
                  </Space>
                </Flex>
              </div>
            )}

            <RichTextEditor
              value={corpo}
              onChange={(html) => { corpoRef.current = html; setSujo(true); }}
              mostrarVariaveis
              variaveis={VARIAVEIS_EXPEDIENTE}
              // A faixa da Pam desconta altura do editor: sem isso a página
              // passa a rolar tendo tela sobrando, que é a mesma regra do modal.
              height={Math.max(300, Math.round(window.innerHeight - 360 - (pamAberta ? 120 : 0)))}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Salvar como template ── */}
      <Modal
        open={tplAberto} onCancel={() => setTplAberto(false)} onOk={salvarComoTemplate}
        confirmLoading={tplSalvando} okText="Salvar template" cancelText="Cancelar"
        title="Salvar este texto como template de contrato"
        width="94vw" style={{ maxWidth: 480, top: 24 }}
      >
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
          O texto atual do documento vira uma minuta com nome, oferecida ao redigir o próximo
          contrato. Usar um nome que já existe atualiza aquela minuta.
        </Text>
        <Input placeholder="Ex.: Prestação de serviço — topobatimetria" value={tplNome}
          maxLength={120} onChange={(e) => setTplNome(e.target.value)}
          onPressEnter={salvarComoTemplate} />
      </Modal>

      <Modal
        open={!!prev} onCancel={() => setPrev(null)} footer={null}
        title="Pré-visualização"
        width="94vw" style={{ maxWidth: 900, top: 16 }}
        styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      >
        {prev?.faltando?.length ? (
          <Alert type="warning" showIcon style={{ marginBottom: 10 }}
            message="Falta preencher antes de finalizar"
            description={prev.faltando.join(' · ')} />
        ) : null}
        {prev?.pendentes?.length ? (
          <Alert type="error" showIcon style={{ marginBottom: 10 }}
            message="Há marcadores sem valor no texto"
            description={<>
              {prev.pendentes.map(p => <Tag key={p} color="red">{`{{${p}}}`}</Tag>)}
              <div style={{ marginTop: 6 }}>
                Preencha os campos correspondentes ou apague os marcadores — o
                sistema não deixa finalizar assim, para o documento não sair com
                lacuna no lugar do dado.
              </div>
            </>} />
        ) : null}
        <div style={{ background: '#fff', padding: 24, border: '1px solid #eee',
                      fontFamily: '"Times New Roman", Times, serif', fontSize: 14, lineHeight: 1.5,
                      textAlign: 'justify' }}
          dangerouslySetInnerHTML={{ __html: prev?.html || '' }} />
      </Modal>
    </div>
  );
}
